import { match } from '@/common/utils/match';
import type { Context } from '@/overmind';

import { isCalculationTacticResultUnavailable } from './actions';
import type { CalculationState } from './createCalculationState';
import type { GridElementsCalculationSetExecutionResults } from './derived-state/gridElementsDetailsByCalculationType/GridElementsDetails';
import { createDetailsWithLoadFlowFilter } from './derived-state/gridElementsDetailsByCalculationType/createDetailsFilteredBySelectedScenarios';
import { DomainGuid } from '.';
import { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

export class Features {
  features: Record<DomainGuid, FeatureWithIDs>;

  constructor(features: FeatureWithIDs[]) {
    this.features = features.reduce<Record<DomainGuid, FeatureWithIDs>>((dictionary, feature) => {
    dictionary[feature.gc.globalId] = feature;
    return dictionary;
  }, {});
  }

  findFeature(objectId: DomainGuid | string): FeatureWithIDs | undefined {
    const globalId = objectId as unknown as DomainGuid;
    const feature = this.features[globalId];
    return feature
  }

  any() {
    return Object.keys(this.features).length > 0;
  }
}

export function createGridElementCalculationSetExecutionDetails(
  state: CalculationState,
  rootState: Context['state']
): CalculationState['gridElementCalculationSetExecutionDetails'] {
  if (state.evaluationRules.type === 'UNAVAILABLE') {
    return { type: 'NO_EVALUATION' };
  }
  if (
    state.calculationTactic.current !== 'LOADED' ||
    state.calculationTactic.value.status !== 'Completed' ||
    isCalculationTacticResultUnavailable(state.calculationTactic.value)
  )
    return { type: 'NO_EVALUATION' };

  const calculationTactic = state.calculationTactic.value;
  const voltageLevel = calculationTactic?.calculationSetResult.voltageLevel;

  const features =
    rootState.arcgis.featureGeometries.current === 'LOADED'
      ? rootState.arcgis.featureGeometries.value.flatMap(
          layer => layer.features
        )
      : [];

  const _features = new Features(features);

  const createDetails = createDetailsWithLoadFlowFilter(
    _features,
    state.calculationTactic.value.calculationSetResult.calculationScenarios,
    state.evaluationRules.rules,
    state.calculationTactic.value,
    voltageLevel,
    rootState.featureToggles
  );
  const allLoadFlowResults =
    state.calculationTactic.value.calculationSetResult.calculationScenarios.loadFlow.map(
      scenario => ({
        scenarioId: scenario.scenarioId,
        scenarioName: scenario.scenarioName,
        scenarioDetails: createDetails(
          loadFlowResult => scenario.scenarioId === loadFlowResult.scenarioId
        ).loadFlow
      })
    );
  const shortCircuit = createDetails().shortCircuit;
  // TODO: Don't filter it here. Create results for each and filter in separate derived state
  const gridElements: GridElementsCalculationSetExecutionResults = match.allOf(
    state.tacticResultBrowsingFilter.type,
    {
      ALL: () => ({
        loadFlow: allLoadFlowResults,
        shortCircuit
      }),
      ALL_LOAD_FLOW: () => ({
        loadFlow: allLoadFlowResults,
        shortCircuit: []
      }),
      SELECTED_LOAD_FLOW: () => ({
        loadFlow: allLoadFlowResults,
        shortCircuit: []
      }),
      SHORT_CIRCUIT: () => ({ shortCircuit, loadFlow: [] }),
      NONE_AVAILABLE: () => ({
        shortCircuit: [],
        loadFlow: []
      })
    }
  );

  return {
    type: 'EVALUATED',
    details: gridElements
  };
}
